import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { CasinoMyRewards } from '@ps/web-martech-cms-types';

import type { CasinoGameService } from '@swf/casino';
import {
  getIcon,
  isFSB,
  preload,
  setLoading,
  userLoggedIn,
  userLoggedOut,
  userUpdates
} from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import type {
  ExtractDispatchFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { CasinoGame, StreamData } from '@swf/core/types/CasinoGame';
import type { InstantBonus } from '@swf/core/types/InstantBonus';
import type { RewardData } from '@swf/core/types/StarsCoinReward';

export interface CasinoMyRewardsWidgetState {
  ib?: InstantBonus[];
  rewards?: RewardData;
  freeSpins?: StreamData<CasinoGame>;
}

const emptyData: CasinoMyRewardsWidgetState = {
  ib: [],
  rewards: { rewards: [], viplevel: 0 },
  freeSpins: { games: [], subscriptions: {} }
};

const reducers = {
  set: (state: CasinoMyRewardsWidgetState, payload: CasinoMyRewardsWidgetState) => ({
    ...state,
    ...payload
  }),
  setLoading
};

const selectors = (slice: Slicer<CasinoMyRewardsWidgetState>) => ({
  get() {
    return slice;
  },

  isLoading() {
    return slice((state) => {
      const result = !(state.freeSpins && state.ib && state.rewards);
      return result;
    });
  },
  getIcon,
  isFSB
});

const effects = {
  preload,
  userLoggedIn,
  userLoggedOut,
  userUpdates,
  async freeSpinBonus(payload: undefined, params: WidgetEffectParams<CasinoMyRewards>) {
    const { widget, dispatch, rootState } = params;
    const widgetDispatch = dispatch[widget.uid];
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);
    if (clientAuthenticated) {
      widgetDispatch.fetchFreeSpins();
    }
  },

  async fetch(payload: undefined, params: WidgetEffectParams<CasinoMyRewards>) {
    const { widget, dispatch, rootState } = params;
    const widgetDispatch = dispatch[widget.uid];
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);
    if (clientAuthenticated) {
      widgetDispatch.fetchFreeSpins();
      widgetDispatch.fetchIB();
      widgetDispatch.fetchRewards();
      widgetDispatch.fetchSpinOfTheDay();
    }
  },
  async fetchFreeSpins(payload: undefined, params: WidgetEffectParams<CasinoMyRewards>) {
    const { dispatch, request, rootState, widget } = params;
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);

    const widgetDispatch: ExtractDispatchFromModel<CasinoMyRewardsWidgetModel> =
      dispatch[widget.uid];

    if (!clientAuthenticated || !widget.free_spins?.config.enabled) {
      widgetDispatch.set({ freeSpins: emptyData.freeSpins });
      return;
    }

    const options = {
      service: widget.free_spins?.config.name as CasinoGameService,
      body: widget.free_spins?.params
    };
    const { body: data } = await request<StreamData<CasinoGame>>(options);
    widgetDispatch.set({ freeSpins: data || { games: [], subscriptions: {} } });
  },
  async fetchIB(payload: undefined, params: WidgetEffectParams<CasinoMyRewards>) {
    const { dispatch, request, rootState, widget } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoMyRewardsWidgetModel> =
      dispatch[widget.uid];
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);
    if (!clientAuthenticated || !widget.ib?.config.enabled) {
      widgetDispatch.set({ ib: emptyData.ib });
      return;
    }

    const options = { service: widget.ib?.config.name as CasinoGameService, body: {} };
    const { body: data } = await request<{ ibs: InstantBonus[] }>(options);
    widgetDispatch.set({ ib: data.ibs || [] });
  },
  async fetchSpinOfTheDay(payload: undefined, params: WidgetEffectParams<CasinoMyRewards>) {
    const { dispatch, widget } = params;
    if (!widget.spin_of_the_day?.config.enabled) {
      dispatch.spinOfTheDay.set({});
      return;
    }

    dispatch.spinOfTheDay.fetch();
  },
  async fetchRewards(payload: undefined, params: WidgetEffectParams<CasinoMyRewards>) {
    const { dispatch, request, rootState, widget } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoMyRewardsWidgetModel> =
      dispatch[widget.uid];
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);
    if (!clientAuthenticated || !widget.rewards?.config.enabled) {
      widgetDispatch.set({ rewards: emptyData.rewards });
      return;
    }

    const options = {
      service: widget.rewards?.config.name as CasinoGameService,
      body: widget.rewards?.params
    };
    const { body: data } = await request<RewardData>(options);
    widgetDispatch.set({ rewards: data || { rewards: [], viplevel: 0 } });
  }
};

export interface CasinoMyRewardsWidgetModel
  extends WidgetModelConfig<CasinoMyRewards, CasinoMyRewardsWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const casinoMyRewardsWidgetModel: CasinoMyRewardsWidgetModel = {
  state: {
    rewards: undefined,
    freeSpins: undefined,
    ib: undefined
  },
  reducers,
  effects,
  selectors
};

export const useCasinoMyRewardsWidgetModel: UseWidget<CasinoMyRewards, CasinoMyRewardsWidgetModel> =
  useWidget;
